// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.TimePicker.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

// REF: https://github.com/palantir/blueprint/blob/develop/packages/datetime/src/timePicker.tsx

import { Classes as CoreClasses, DISPLAYNAME_PREFIX, Keys, Utils as BlueprintUtils } from '@blueprintjs/core';
import classNames from 'classnames';
import React, { Component } from 'react';
import { isNil, isUndefined, cloneDeep } from 'lodash/fp';
import Moment from 'moment';

const NS = CoreClasses.getClassNamespace();
const TIMEPICKER = `${NS}-timepicker`;
const TIMEPICKER_INPUT_ROW = `${TIMEPICKER}-input-row`;
const TIMEPICKER_MINUTE = `${TIMEPICKER}-minute`;
const TIMEPICKER_SECOND = `${TIMEPICKER}-second`;
const TIMEPICKER_HOUR = `${TIMEPICKER}-hour`;
const TIMEPICKER_INPUT = `${TIMEPICKER}-input`;
const TIMEPICKER_DIVIDER_TEXT = `${TIMEPICKER}-divider-text`;

const DEFAULT_MIN_HOUR = 0;
const DEFAULT_MIN_MINUTE = 0;
const DEFAULT_MIN_SECOND = 0;
const DEFAULT_MIN_MILLISECOND = 0;

const DEFAULT_MAX_HOUR = 23;
const DEFAULT_MAX_MINUTE = 59;
const DEFAULT_MAX_SECOND = 59;
const DEFAULT_MAX_MILLISECOND = 999;

const TimeUnit = {
  HOUR_24: 'hour24',
  MINUTE: 'minute',
  SECOND: 'second'
};

const TimeUnitMetadata = {
  [TimeUnit.HOUR_24]: {
    className: TIMEPICKER_HOUR,
    max: DEFAULT_MAX_HOUR,
    min: DEFAULT_MIN_HOUR,
    placeHolder: 'HH'
  },
  [TimeUnit.MINUTE]: {
    className: TIMEPICKER_MINUTE,
    max: DEFAULT_MAX_MINUTE,
    min: DEFAULT_MIN_MINUTE,
    placeHolder: 'mm'
  },
  [TimeUnit.SECOND]: {
    className: TIMEPICKER_SECOND,
    max: DEFAULT_MAX_SECOND,
    min: DEFAULT_MIN_SECOND,
    placeHolder: 'ss'
  }
};

const TimePrecision = {
  MINUTE: 'minute',
  SECOND: 'second'
};

export default class TimePicker extends Component {
  static defaultProps = {
    disabled: false,
    maxTime: getDefaultMaxTime(),
    minTime: getDefaultMinTime(),
    precision: TimePrecision.SECOND,
    selectAllOnFocus: false
  };

  static displayName = `${DISPLAYNAME_PREFIX}.TimePicker`;

  constructor(props) {
    super(props);

    let value = props.defaultValue;
    if (!isUndefined(props.value)) {
      value = props.value;
    }

    this.state = this.getFullStateFromValue(value);
    this.clonedState = cloneDeep(this.state);
  }

  render() {
    const classes = classNames(TIMEPICKER, this.props.className, {
      [CoreClasses.DISABLED]: this.props.disabled
    });

    return (
      <div className={classes}>
        <div className={TIMEPICKER_INPUT_ROW}>
          {this.renderInput(TIMEPICKER_HOUR, TimeUnit.HOUR_24, this.state.hourText)}
          {this.renderDivider()}
          {this.renderInput(TIMEPICKER_MINUTE, TimeUnit.MINUTE, this.state.minuteText)}
          {this.renderDivider()}
          {this.renderInput(TIMEPICKER_SECOND, TimeUnit.SECOND, this.state.secondText)}
        </div>
      </div>
    );
  }

  componentWillReceiveProps(nextProps) {
    const didMinTimeChange = nextProps.minTime !== this.props.minTime;
    const didMaxTimeChange = nextProps.maxTime !== this.props.maxTime;
    const didBoundsChange = didMinTimeChange || didMaxTimeChange;

    let value = this.state.value;
    if (didBoundsChange) {
      value = getTimeInRange(this.state.value, nextProps.minTime, nextProps.maxTime);
    }
    if (!areSameTime(nextProps.value, this.props.value)) {
      value = nextProps.value;
    }

    this.setState(this.getFullStateFromValue(value));
  }

  renderDivider(text = ':') {
    return <span className={TIMEPICKER_DIVIDER_TEXT}>{text}</span>;
  }

  renderInput(className, unit, value) {
    return (
      <input
        className={classNames(TIMEPICKER_INPUT, className)}
        onBlur={this.getInputBlurHandler(unit)}
        onChange={this.getInputChangeHandler(unit)}
        onFocus={this.handleFocus}
        onKeyDown={this.getInputKeyDownHandler(unit)}
        value={value}
        disabled={this.props.disabled}
        placeholder={getPlaceHolder(unit)}
      />
    );
  }

  getInputBlurHandler = unit => e => {
    const text = getStringValueFromInputEvent(e);
    let value = parseInt(text, 10);
    if (text === '') value = '';

    this.updateTime(value, unit);
  };

  getInputChangeHandler = unit => e => {
    const TWO_DIGITS = /^\d{0,2}$/;
    const text = getStringValueFromInputEvent(e);

    let isValid = false;
    switch (unit) {
      case TimeUnit.HOUR_24:
      case TimeUnit.MINUTE:
      case TimeUnit.SECOND:
        isValid = TWO_DIGITS.test(text);
        break;
      default:
        throw Error('Invalid TimeUnit');
    }

    if (isValid) {
      switch (unit) {
        case TimeUnit.HOUR_24:
          this.updateState({ hourText: text });
          break;
        case TimeUnit.MINUTE:
          this.updateState({ minuteText: text });
          break;
        case TimeUnit.SECOND:
          this.updateState({ secondText: text });
          break;
        default:
          throw Error('Invalid TimeUnit');
      }
    }
  };

  getInputKeyDownHandler = unit => e => {
    handleKeyEvent(e, {
      [Keys.ARROW_UP]: () => this.incrementTime(unit),
      [Keys.ARROW_DOWN]: () => this.decrementTime(unit),
      [Keys.ENTER]: () => {
        e.currentTarget.blur();
      }
    });
  };

  handleFocus = e => {
    if (this.props.selectAllOnFocus) {
      e.currentTarget.select();
    }
    this.clonedState = cloneDeep(this.state);
  };

  getFullStateFromValue(value) {
    const timeInRange = getTimeInRange(value, this.props.minTime, this.props.maxTime);
    return {
      hourText: timeInRange ? formatTime(timeInRange.getHours(), TimeUnit.HOUR_24) : '',
      minuteText: timeInRange ? formatTime(timeInRange.getMinutes(), TimeUnit.MINUTE) : '',
      secondText: timeInRange ? formatTime(timeInRange.getSeconds(), TimeUnit.SECOND) : '',
      value: timeInRange
    };
  }

  incrementTime = unit => this.shiftTime(unit, 1);
  decrementTime = unit => this.shiftTime(unit, -1);
  shiftTime(unit, amount) {
    if (this.props.disabled || !this.state.value) {
      return;
    }
    const newTime = getTimeUnit(unit, this.state.value) + amount;
    this.updateTime(wrapTimeAtUnit(unit, newTime), unit);
  }

  updateTime(time, unit) {
    let newValue = this.state.value ? clone(this.state.value) : null;
    if (isNil(this.state.value)) {
      newValue = Moment({ h: 0, m: 0, s: 0, ms: 0 }).toDate();
    }

    if (isTimeUnitValid(unit, time)) {
      setTimeUnit(unit, time, newValue);
      if (isTimeInRange(newValue, this.props.minTime, this.props.maxTime)) {
        this.updateState({ value: newValue });
      } else {
        this.updateState(this.getFullStateFromValue(this.state.value));
      }
    } else if (time === '') {
      this.clonedState.hourText = '';
      this.clonedState.minuteText = '';
      this.clonedState.secondText = '';
      this.clonedState.value = null;
      this.updateState(this.clonedState, true);
    } else {
      this.updateState(this.getFullStateFromValue(this.state.value));
    }
  }

  updateState(state, force) {
    let newState = state;
    const hasNewValue = newState.value != null && !areSameTime(newState.value, this.state.value);

    if (this.props.value == null) {
      // component is uncontrolled
      if (hasNewValue) {
        newState = this.getFullStateFromValue(newState.value);
      }
      this.setState(newState);
    } else {
      // component is controlled, and there's a new value
      // so set inputs' text based off of _old_ value and later fire onChange with new value
      if (hasNewValue) {
        this.setState(this.getFullStateFromValue(this.state.value));
      } else {
        // no new value, this means only text has changed (from user typing)
        // we want inputs to change, so update state with new text for the inputs
        // but don't change actual value
        this.setState({ ...newState, value: clone(this.state.value) });
      }
    }

    if (hasNewValue || force) {
      BlueprintUtils.safeInvoke(this.props.onChange, newState.value);
    }
  }
}

function formatTime(time, unit) {
  switch (unit) {
    case TimeUnit.HOUR_24:
      return time.toString();
    case TimeUnit.MINUTE:
    case TimeUnit.SECOND:
      return padWithZeroes(time.toString(), 2);
    default:
      throw Error('Invalid TimeUnit');
  }
}

function getStringValueFromInputEvent(e) {
  return e.target.value;
}

function handleKeyEvent(e, actions, preventDefault = true) {
  for (const k of Object.keys(actions)) {
    const key = Number(k);
    if (e.which === key) {
      if (preventDefault) {
        e.preventDefault();
      }
      actions[key]();
    }
  }
}

function areSameTime(date1, date2) {
  return (
    date1 != null &&
    date2 != null &&
    date1.getHours() === date2.getHours() &&
    date1.getMinutes() === date2.getMinutes() &&
    date1.getSeconds() === date2.getSeconds() &&
    date1.getMilliseconds() === date2.getMilliseconds()
  );
}

function clone(d) {
  return new Date(d.getTime());
}

const isTimeEqualOrGreaterThan = (time, timeToCompare) => time.getTime() >= timeToCompare.getTime();
const isTimeEqualOrSmallerThan = (time, timeToCompare) => time.getTime() <= timeToCompare.getTime();

function isTimeInRange(date, minDate, maxDate) {
  const time = getDateOnlyWithTime(date);
  const minTime = getDateOnlyWithTime(minDate);
  const maxTime = getDateOnlyWithTime(maxDate);

  const isTimeGreaterThanMinTime = isTimeEqualOrGreaterThan(time, minTime);
  const isTimeSmallerThanMaxTime = isTimeEqualOrSmallerThan(time, maxTime);

  if (isTimeEqualOrSmallerThan(maxTime, minTime)) {
    return isTimeGreaterThanMinTime || isTimeSmallerThanMaxTime;
  }

  return isTimeGreaterThanMinTime && isTimeSmallerThanMaxTime;
}

function getTimeInRange(time, minTime, maxTime) {
  if (isNil(time)) return time;
  if (areSameTime(minTime, maxTime)) {
    return maxTime;
  } else if (isTimeInRange(time, minTime, maxTime)) {
    return time;
  } else if (isTimeSameOrAfter(time, maxTime)) {
    return maxTime;
  }

  return minTime;
}

/**
 * Returns true if the time part of `date` is later than or equal to the time
 * part of `dateToCompare`. The day, month, and year parts will not be compared.
 */
function isTimeSameOrAfter(date, dateToCompare) {
  const time = getDateOnlyWithTime(date);
  const timeToCompare = getDateOnlyWithTime(dateToCompare);

  return isTimeEqualOrGreaterThan(time, timeToCompare);
}

function getDateOnlyWithTime(date) {
  return new Date(0, 0, 0, date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
}



/** Returns the given time unit component of the date. */
function getTimeUnit(unit, date) {
  switch (unit) {
    case TimeUnit.HOUR_24:
      return date.getHours();
    case TimeUnit.MINUTE:
      return date.getMinutes();
    case TimeUnit.SECOND:
      return date.getSeconds();
    default:
      throw Error('Invalid TimeUnit');
  }
}

/** Sets the given time unit to the given time in date object. Modifies given `date` object and returns it. */
function setTimeUnit(unit, time, date) {
  switch (unit) {
    case TimeUnit.HOUR_24:
      date.setHours(time);
      break;
    case TimeUnit.MINUTE:
      date.setMinutes(time);
      break;
    case TimeUnit.SECOND:
      date.setSeconds(time);
      break;
    default:
      throw Error('Invalid TimeUnit');
  }
  return date;
}

/** Returns true if `time` is a valid value */
function isTimeUnitValid(unit, time) {
  return time !== '' && time != null && !isNaN(time) && getTimeUnitMin(unit) <= time && time <= getTimeUnitMax(unit);
}

/** If unit of time is greater than max, returns min. If less than min, returns max. Otherwise, returns time. */
function wrapTimeAtUnit(unit, time) {
  const max = getTimeUnitMax(unit);
  const min = getTimeUnitMin(unit);

  if (time > max) {
    return min;
  } else if (time < min) {
    return max;
  }
  return time;
}

function getTimeUnitMax(unit) {
  return TimeUnitMetadata[unit].max;
}

function getTimeUnitMin(unit) {
  return TimeUnitMetadata[unit].min;
}

function getPlaceHolder(unit) {
  return TimeUnitMetadata[unit].placeHolder;
}

function getDefaultMinTime() {
  return new Date(0, 0, 0, DEFAULT_MIN_HOUR, DEFAULT_MIN_MINUTE, DEFAULT_MIN_SECOND, DEFAULT_MIN_MILLISECOND);
}

function getDefaultMaxTime() {
  return new Date(0, 0, 0, DEFAULT_MAX_HOUR, DEFAULT_MAX_MINUTE, DEFAULT_MAX_SECOND, DEFAULT_MAX_MILLISECOND);
}

function padWithZeroes(str, minLength) {
  if (str.length < minLength) {
    return `${stringRepeat('0', minLength - str.length)}${str}`;
  } else {
    return str;
  }
}

function stringRepeat(str, numTimes) {
  return new Array(numTimes + 1).join(str);
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/DateTime/Izenda.TimePicker.js